import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ExternalLink, Key, Loader2, Check, AlertCircle, Sparkles } from "lucide-react";
import clsx from "clsx";
import { useStore } from "../../stores/store";
import { api } from "../../lib/api";

interface Props {
  open: boolean;
  onClose: () => void;
  canSkip?: boolean;
}

export function OnboardingGroq({ open, onClose, canSkip = true }: Props) {
  const [key, setKey] = useState("");
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<"idle" | "ok" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const saveGroqKey = useStore((s) => s.saveGroqKey);

  const valid = /^gsk_[A-Za-z0-9_-]{20,}$/.test(key.trim());

  async function handleSave() {
    setError(null);
    if (!valid) {
      setError("Formato inválido. Chave Groq começa com gsk_");
      return;
    }
    setTesting(true);
    try {
      const { ok } = await api.testGroqKey(key.trim());
      if (!ok) {
        setStatus("error");
        setError("Chave rejeitada pela Groq. Verifique e tente de novo.");
        setTesting(false);
        return;
      }
      setStatus("ok");
      setTesting(false);
      setSaving(true);
      await saveGroqKey(key.trim());
      setSaving(false);
      setTimeout(onClose, 600);
    } catch (e: any) {
      setStatus("error");
      setError(e.message || "Erro ao validar chave");
      setTesting(false);
      setSaving(false);
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[55] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-md"
            onClick={canSkip ? onClose : undefined}
          />
          <motion.div
            className="glass relative w-full max-w-none overflow-hidden rounded-none p-5 shadow-elevated md:w-[560px] md:max-w-[94vw] md:rounded-[22px] md:p-7"
            initial={{ scale: 0.92, y: 16, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, y: 10, opacity: 0 }}
            transition={{ type: "spring", damping: 22, stiffness: 240 }}
          >
            <div className="pointer-events-none absolute inset-0 mesh-drift opacity-50" />
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-400/60 to-transparent" />

            {canSkip && (
              <button
                onClick={onClose}
                className="absolute right-4 top-4 z-10 rounded-lg p-1.5 text-slate-400 transition hover:bg-white/5 hover:text-slate-100"
              >
                <X className="h-4 w-4" />
              </button>
            )}

            <div className="relative flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.18em] text-violet-300/90">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-gradient-to-br from-violet-500/40 to-fuchsia-500/40 ring-1 ring-violet-400/40">
                <Sparkles className="h-3 w-3" />
              </span>
              Configuração da IA
            </div>
            <h2 className="relative mt-1 text-2xl font-semibold tracking-tight text-slate-100">
              Conecte sua chave Groq em 1 minuto
            </h2>
            <p className="relative mt-1.5 text-sm leading-relaxed text-slate-400">
              O on_board usa a Groq para transcrever sua voz e organizar suas tarefas. A chave fica{" "}
              <span className="font-medium text-violet-300">criptografada</span> no seu perfil e nunca é
              compartilhada. Tudo <span className="font-medium">grátis</span> até o limite generoso do free tier.
            </p>

            <ol className="relative mt-5 space-y-3">
              <Step n={1}>
                Crie uma conta grátis em{" "}
                <a
                  href="https://console.groq.com/signup"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-violet-300 underline decoration-violet-400/40 underline-offset-2 hover:decoration-violet-400"
                >
                  console.groq.com <ExternalLink className="h-3 w-3" />
                </a>
              </Step>
              <Step n={2}>
                No menu lateral, clique em <span className="rounded bg-white/5 px-1.5 py-0.5 font-mono text-[11px]">API Keys</span> → <span className="rounded bg-white/5 px-1.5 py-0.5 font-mono text-[11px]">Create API Key</span>
              </Step>
              <Step n={3}>Dê um nome (ex: "on_board"), copie a chave que começa com <span className="rounded bg-white/5 px-1.5 py-0.5 font-mono text-[11px]">gsk_…</span></Step>
              <Step n={4}>Cole aqui:</Step>
            </ol>

            <div className="relative mt-3">
              <div
                className={clsx(
                  "flex items-center gap-2 rounded-lg border bg-black/30 px-3 py-2 transition",
                  status === "ok"
                    ? "border-emerald-400/60 shadow-[0_0_0_3px_rgba(16,185,129,0.15)]"
                    : status === "error"
                      ? "border-rose-400/60 shadow-[0_0_0_3px_rgba(244,63,94,0.15)]"
                      : "border-white/10 focus-within:border-violet-500/60 focus-within:shadow-[0_0_0_3px_rgba(168,85,247,0.12)]",
                )}
              >
                <Key className="h-4 w-4 shrink-0 text-slate-400" />
                <input
                  type="password"
                  autoFocus
                  value={key}
                  onChange={(e) => {
                    setKey(e.target.value);
                    setStatus("idle");
                    setError(null);
                  }}
                  onKeyDown={(e) => e.key === "Enter" && valid && !testing && !saving && handleSave()}
                  placeholder="gsk_..."
                  autoComplete="off"
                  spellCheck={false}
                  className="flex-1 bg-transparent font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none"
                />
                {status === "ok" && <Check className="h-4 w-4 text-emerald-400" />}
                {status === "error" && <AlertCircle className="h-4 w-4 text-rose-400" />}
              </div>
              {error && <p className="mt-2 text-xs text-rose-300">{error}</p>}
            </div>

            <div className="relative mt-6 flex items-center justify-between gap-3">
              {canSkip ? (
                <button
                  onClick={onClose}
                  className="text-xs text-slate-500 hover:text-slate-300"
                >
                  Pular por enquanto
                </button>
              ) : (
                <span />
              )}
              <motion.button
                whileHover={valid && !testing && !saving ? { scale: 1.02 } : {}}
                whileTap={valid && !testing && !saving ? { scale: 0.97 } : {}}
                onClick={handleSave}
                disabled={!valid || testing || saving}
                className="group relative flex items-center gap-1.5 overflow-hidden rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 px-4 py-2 text-sm font-medium text-white shadow-[0_0_22px_rgba(168,85,247,0.4)] transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                {testing ? (
                  <>
                    <Loader2 className="relative h-3.5 w-3.5 animate-spin" /> <span className="relative">testando</span>
                  </>
                ) : saving ? (
                  <>
                    <Loader2 className="relative h-3.5 w-3.5 animate-spin" /> <span className="relative">salvando</span>
                  </>
                ) : (
                  <>
                    <Check className="relative h-3.5 w-3.5" /> <span className="relative">Testar e salvar</span>
                  </>
                )}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3 text-sm text-slate-300">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-violet-500/20 font-mono text-[10px] font-semibold text-violet-200 ring-1 ring-violet-400/30">
        {n}
      </span>
      <span className="leading-relaxed">{children}</span>
    </li>
  );
}
